import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { msalConfig } from './authConfig'
import { Toaster } from './components/ui/sonner'
import PlaceholderPage from './pages/PlaceholderPage'
import 'react-day-picker/dist/style.css'
import './index.css'

const App = lazy(() => import('./App.tsx'))
const AuthGate = lazy(() => import('./components/AuthGate'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const NetworkSearchPage = lazy(() => import('./pages/NetworkSearchPage'))
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage'))
const OrganizationDetailPage = lazy(() => import('./pages/OrganizationDetailPage'))
const OrganizationEditPage = lazy(() => import('./pages/OrganizationEditPage'))
const VendorsPage = lazy(() => import('./pages/VendorsPage'))
const VendorDetailPage = lazy(() => import('./pages/VendorDetailPage'))
const LocationsPage = lazy(() => import('./pages/LocationsPage'))
const LocationDetailPage = lazy(() => import('./pages/LocationDetailPage'))
const ProvidersPage = lazy(() => import('./pages/ProvidersPage'))
const ProviderDetailPage = lazy(() => import('./pages/ProviderDetailPage'))
const ProviderEditPage = lazy(() => import('./pages/ProviderEditPage'))
const ProviderLocationsPage = lazy(() => import('./pages/ProviderLocationsPage'))
const ProviderLocationEditPage = lazy(() => import('./pages/ProviderLocationEditPage'))
const VendorEditPage = lazy(() => import('./pages/VendorEditPage'))
const ProviderUpdateEditPage = lazy(() => import('./pages/ProviderUpdateEditPage'))
const FacilitiesPage = lazy(() => import('./pages/FacilitiesPage'))
const FacilityDetailPage = lazy(() => import('./pages/FacilityDetailPage'))
const FacilityEditPage = lazy(() => import('./pages/FacilityEditPage'))
const UpdateFormPage = lazy(() => import('./pages/UpdateFormPage'))

const msalInstance = new PublicClientApplication(msalConfig)

async function bootstrap() {
  try {
    await msalInstance.initialize()
    const redirectResult = await msalInstance.handleRedirectPromise()
    const activeAccount =
      redirectResult?.account ??
      msalInstance.getActiveAccount() ??
      msalInstance.getAllAccounts()[0] ??
      null

    if (activeAccount) {
      msalInstance.setActiveAccount(activeAccount)
    }
  } catch (error) {
    console.error('MSAL redirect handling failed', error)
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-600">Loading...</div>}>
            <Routes>
              <Route
                path="/"
                element={
                  <AuthGate>
                    <App />
                  </AuthGate>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="network-search" element={<NetworkSearchPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/:identifier" element={<OrganizationDetailPage />} />
                <Route path="organizations/:identifier/edit" element={<OrganizationEditPage />} />
                <Route path="vendors" element={<VendorsPage />} />
                <Route path="vendors/:npi" element={<VendorDetailPage />} />
                <Route path="providers" element={<ProvidersPage />} />
                <Route path="providers/:npi/detail" element={<ProviderDetailPage />} />
                <Route path="providers/:npi/detail/edit" element={<ProviderEditPage />} />
                <Route path="facilities" element={<FacilitiesPage />} />
                <Route path="facilities/:id" element={<FacilityDetailPage />} />
                <Route path="facilities/:id/edit" element={<FacilityEditPage />} />
                <Route path="locations" element={<LocationsPage />} />
                <Route path="locations/:id" element={<LocationDetailPage />} />
                <Route path="contracts" element={<PlaceholderPage title="Contracts" />} />
                <Route path="reports" element={<PlaceholderPage title="Reports" subtitle="Reporting tools will appear here." />} />
                <Route path="administration" element={<PlaceholderPage title="Administration" />} />
                <Route path="providers/:npi/locations" element={<ProviderLocationsPage />} />
                <Route path="locations/:id/edit" element={<ProviderLocationEditPage />} />
                <Route path="vendors/:npi/edit" element={<VendorEditPage />} />
                <Route path="provider-updates/:id/edit" element={<ProviderUpdateEditPage />} />
              </Route>
              <Route
                path="/auth-preview"
                element={
                  <AuthGate autoRedirect={false} showPromptWhenAuthenticated>
                    <div />
                  </AuthGate>
                }
              />
              <Route path="/Provider-Update" element={<UpdateFormPage />} />
            </Routes>
          </Suspense>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>,
  )
}

void bootstrap()
